import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import type { DealFormDataWithTargeting } from '@openx/types/deal';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealFormContext } from '../DealForm.context';

type UseDealFeeReminderPopupResult = {
  verifyFeeReminderContinuation: (formValues: DealFormDataWithTargeting) => Promise<boolean>;
};

export const useDealFeeReminderPopup = (): UseDealFeeReminderPopupResult => {
  const { open, close } = useConfirmationDialog();
  const { thirdPartyPartnerOptions, thirdPartyPartnerOptionsLoading } = useDealFormContext();
  const popupResolveRef = useRef<(() => void) | null>(null);
  const popupRejectRef = useRef<(() => void) | null>(null);
  const pendingDecisionRef = useRef<Promise<boolean> | null>(null);
  const pendingValuesRef = useRef<DealFormDataWithTargeting | null>(null);
  const { t } = useTranslation();

  const hasAnyPartner = useMemo(() => Object.keys(thirdPartyPartnerOptions).length !== 0, [thirdPartyPartnerOptions]);

  const finalizeDecision = useCallback(
    (decision: boolean) => {
      const resolve = popupResolveRef.current;
      const reject = popupRejectRef.current;

      popupResolveRef.current = null;
      popupRejectRef.current = null;
      pendingDecisionRef.current = null;
      pendingValuesRef.current = null;
      close?.();

      if (decision) {
        resolve?.();
      } else {
        reject?.();
      }
    },
    [close]
  );

  const handleCancel = useCallback(() => {
    finalizeDecision(false);
  }, [finalizeDecision]);

  const handleConfirm = useCallback(() => {
    finalizeDecision(true);
  }, [finalizeDecision]);

  const handleDecision = useCallback(
    (formValues: DealFormDataWithTargeting) => {
      const hasThirdPartyFeesConfig = formValues.third_party_fees_config_enabled;

      const needsPopup = !hasThirdPartyFeesConfig && hasAnyPartner;

      if (needsPopup) {
        open({
          content: (
            <Box sx={{ p: 2 }} data-test="dialog-content">
              <Typography>{t('This deal does not have a Curated Deal Fee applied.')}</Typography>
              <Typography variant="body1Bold">{t('Would you like to save the deal anyway?')}</Typography>
            </Box>
          ),
          customContent: true,
          confirmationLabel: t('Yes - Save without Curated Deal Fee'),
          cancelLabel: t('No - Apply Curated Deal Fee'),
          showExtraBtn: false,
          title: t('Confirmation'),
          onCancel: handleCancel,
          onConfirmed: handleConfirm,
        });
      } else {
        finalizeDecision(true);
      }
    },
    [finalizeDecision, handleConfirm, handleCancel, hasAnyPartner, open, t]
  );

  const verifyFeeReminderContinuation = useCallback(
    (formValues: DealFormDataWithTargeting): Promise<boolean> => {
      const hasThirdPartyFeesConfig = formValues.third_party_fees_config_enabled;

      const needsPopup = !hasThirdPartyFeesConfig && hasAnyPartner;

      if (!thirdPartyPartnerOptionsLoading && !needsPopup) {
        popupResolveRef.current = null;
        popupRejectRef.current = null;
        pendingDecisionRef.current = null;
        pendingValuesRef.current = null;
        close?.();
        return Promise.resolve(true);
      }

      if (pendingDecisionRef.current) {
        return pendingDecisionRef.current;
      }

      const decisionPromise = new Promise<boolean>(resolve => {
        popupResolveRef.current = () => resolve(true);
        popupRejectRef.current = () => resolve(false);

        if (thirdPartyPartnerOptionsLoading) {
          pendingValuesRef.current = formValues;
        } else {
          handleDecision(formValues);
        }
      });

      pendingDecisionRef.current = decisionPromise;

      return decisionPromise;
    },
    [close, handleDecision, hasAnyPartner, thirdPartyPartnerOptionsLoading]
  );

  useEffect(() => {
    if (!thirdPartyPartnerOptionsLoading && pendingValuesRef.current) {
      const values = pendingValuesRef.current;
      pendingValuesRef.current = null;
      handleDecision(values);
    }
  }, [handleDecision, thirdPartyPartnerOptionsLoading]);

  return {
    verifyFeeReminderContinuation,
  };
};
